import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, finalize, map, of, shareReplay, switchMap, take, throwError } from 'rxjs';
import type { AdminLoginCredentials } from '../models/login.model';
import { environment } from '../../../../../environments/environment';

type AdminAuthRole = 'admin' | 'advertiser';

type AdminAuthUser = {
  id: number;
  name: string;
  email: string;
  status: 'pending' | 'active' | 'inactive';
  company_name?: string | null;
  phone?: string | null;
};

type AdminAuthSession = {
  token: string | null;
  user: AdminAuthUser;
  role: AdminAuthRole;
  permissions: string[];
};

type AdminLoginResponse = {
  success: boolean;
  message: string;
  data:
    | {
        token: string;
        user: AdminAuthUser;
        role?: AdminAuthRole;
        permissions: string[];
      }
    | {
        otpRequired: true;
        otpToken: string;
      };
};

type AdminMeResponse = {
  success: boolean;
  message: string;
  data: {
    user: AdminAuthUser;
    role?: AdminAuthRole;
    permissions: string[];
  };
};

type AdminRefreshResponse = {
  success: boolean;
  message: string;
  data: {
    token: string;
  };
};

type AdminBasicResponse = {
  success: boolean;
  message: string;
  data: null;
};

const ADMIN_ROUTE_CANDIDATES: { url: string; permissions: string[] }[] = [
  { url: '/admin/settings/advertisers', permissions: ['advertisers.manage.view'] },
  { url: '/admin/dashboard', permissions: ['dashboard.view'] },
  { url: '/admin/clients', permissions: ['clients.list'] },
  { url: '/admin/profiling', permissions: ['profiling.view'] },
  { url: '/admin/events/realtime', permissions: ['events.realtime.view'] },
  { url: '/admin/events/history', permissions: ['events.history.view'] },
  { url: '/admin/analytics/subscriptions', permissions: ['analytics.subscriptions.view'] },
  { url: '/admin/analytics/editorial', permissions: ['analytics.editorial.view'] },
  { url: '/admin/analytics/user-stats', permissions: ['analytics.users.view'] },
  { url: '/admin/analytics/profiling-stats', permissions: ['analytics.profiling.view'] },
  { url: '/admin/paywall/rules', permissions: ['paywall.rules.view'] },
  { url: '/admin/payments', permissions: ['payments.view'] },
  { url: '/admin/logs', permissions: ['logs.view'] },
  { url: '/admin/settings/admins', permissions: ['settings.admins.view'] },
  { url: '/admin/settings/parameters', permissions: ['settings.parameters.view'] },
  { url: '/admin/settings/plans', permissions: ['settings.plans.view'] },
  { url: '/admin/settings/profiling-fields', permissions: ['settings.profiling-fields.view'] },
  { url: '/admin/settings/event-types', permissions: ['settings.event-types.view'] },
  { url: '/admin/docs', permissions: ['docs.view'] },
  { url: '/admin/account', permissions: ['account.view'] },
];

@Injectable({ providedIn: 'root' })
export class AdminAuthService {
  private readonly http: HttpClient;
  private refreshPermissionsRequest$?: Observable<string[]>;
  private refreshAccessTokenRequest$?: Observable<boolean>;
  private sessionRestoreRequest$?: Observable<boolean>;
  private _pendingToken: string | null = null;
  private didManualLogout = false;

  private readonly sessionSubject = new BehaviorSubject<AdminAuthSession | null>(null);
  readonly session$ = this.sessionSubject.asObservable();

  constructor(http: HttpClient) {
    this.http = http;
  }

  get token(): string | null {
    return this.sessionSubject.value?.token ?? null;
  }

  get currentUser(): AdminAuthUser | null {
    return this.sessionSubject.value?.user ?? null;
  }

  get permissions(): string[] {
    return this.sessionSubject.value?.permissions ?? [];
  }

  get role(): AdminAuthRole | null {
    return this.sessionSubject.value?.role ?? null;
  }

  isAdvertiser(): boolean {
    return this.role === 'advertiser';
  }

  isAdmin(): boolean {
    return this.role === 'admin';
  }

  isAuthenticated(): boolean {
    if (this.didManualLogout) return false;
    return !!this.sessionSubject.value?.user;
  }

  login(payload: AdminLoginCredentials): Observable<AdminAuthSession | { otpRequired: true; otpToken: string }> {
    return this.http
      .post<AdminLoginResponse>(`${environment.apiUrl}/admin/auth/login`, payload, { withCredentials: true })
      .pipe(
        switchMap(res => {
          const data: any = res?.data;
          if (data?.otpRequired === true && typeof data?.otpToken === 'string' && data.otpToken.trim()) {
            return of({ otpRequired: true as const, otpToken: String(data.otpToken) });
          }

          const token = data?.token;
          const user = data?.user;
          const role: AdminAuthRole = data?.role === 'advertiser' ? 'advertiser' : 'admin';
          const permissions = Array.isArray(data?.permissions) ? data.permissions : [];
          if (!token || !user) return throwError(() => new Error('Respuesta de login inválida'));
          this.didManualLogout = false;
          const initial: AdminAuthSession = { token, user, role, permissions };
          this.setSession(initial);
          return of(this.sessionSubject.value!);
        }),
      );
  }

  verifyLoginOtp(otpToken: string, code: string): Observable<AdminAuthSession> {
    return this.http
      .post<AdminLoginResponse>(
        `${environment.apiUrl}/admin/auth/login/otp`,
        { otpToken, code },
        { withCredentials: true },
      )
      .pipe(
        switchMap(res => {
          const data: any = res?.data;
          const token = data?.token;
          const user = data?.user;
          const role: AdminAuthRole = data?.role === 'advertiser' ? 'advertiser' : 'admin';
          const permissions = Array.isArray(data?.permissions) ? data.permissions : [];
          if (!token || !user) return throwError(() => new Error('Respuesta de login inválida'));
          this.didManualLogout = false;
          this.setSession({ token, user, role, permissions });
          return of(this.sessionSubject.value!);
        }),
      );
  }

  logout(syncServer = true): void {
    this.didManualLogout = true;
    if (syncServer) {
      this.http
        .post(`${environment.apiUrl}/admin/auth/logout`, {}, { withCredentials: true })
        .pipe(
          take(1),
          catchError(() => of(null)),
        )
        .subscribe();
    }
    this.clearSession();
  }

  requestPasswordReset(email: string): Observable<void> {
    return this.http
      .post<AdminBasicResponse>(`${environment.apiUrl}/admin/auth/password/forgot`, { email }, { withCredentials: true })
      .pipe(map(() => undefined));
  }

  resetPassword(token: string, password: string): Observable<void> {
    return this.http
      .post<AdminBasicResponse>(
        `${environment.apiUrl}/admin/auth/password/reset`,
        { token, password },
        { withCredentials: true },
      )
      .pipe(map(() => undefined));
  }

  resetAdvertiserPassword(token: string, password: string): Observable<void> {
    return this.http
      .post<AdminBasicResponse>(
        `${environment.apiUrl}/admin/auth/advertiser/password/reset`,
        { token, password },
        { withCredentials: true },
      )
      .pipe(map(() => undefined));
  }

  activateAdvertiser(token: string, password: string): Observable<void> {
    return this.http
      .post<AdminBasicResponse>(
        `${environment.apiUrl}/admin/auth/advertiser/activate`,
        { token, password },
        { withCredentials: true },
      )
      .pipe(map(() => undefined));
  }

  refreshCurrentUserPermissions(): Observable<string[]> {
    if (this.didManualLogout) return of([]);
    if (this.refreshPermissionsRequest$) return this.refreshPermissionsRequest$;

    this.refreshPermissionsRequest$ = this.ensureSession().pipe(
      map(ok => (ok ? this.permissions : [])),
      catchError(() => of(this.permissions)),
      finalize(() => {
        this.refreshPermissionsRequest$ = undefined;
      }),
      shareReplay(1),
    );

    return this.refreshPermissionsRequest$;
  }

  refreshAccessToken(): Observable<boolean> {
    if (this.didManualLogout) return of(false);
    if (this.refreshAccessTokenRequest$) return this.refreshAccessTokenRequest$;

    this.refreshAccessTokenRequest$ = this.http
      .post<AdminRefreshResponse>(`${environment.apiUrl}/admin/auth/refresh`, {}, { withCredentials: true })
      .pipe(
        map(res => {
          const newToken = res?.data?.token;
          if (newToken) {
            const current = this.sessionSubject.value;
            if (current) {
              this.setSession({ ...current, token: newToken });
            } else {
              this._pendingToken = newToken;
            }
          }
          return Boolean(newToken);
        }),
        catchError(() => {
          this.clearSession();
          return of(false);
        }),
        finalize(() => {
          this.refreshAccessTokenRequest$ = undefined;
        }),
        shareReplay(1),
      );

    return this.refreshAccessTokenRequest$;
  }

  getDefaultAdminUrl(): string | null {
    if (this.isAdvertiser()) return '/admin/advertiser-portal/campaigns';

    for (const candidate of ADMIN_ROUTE_CANDIDATES) {
      if (this.hasAllPermissions(candidate.permissions)) return candidate.url;
    }
    return null;
  }

  hasPermission(requiredKey: string): boolean {
    if (this.isAdvertiser()) return false;
    const normalized = (requiredKey ?? '').trim();
    if (!normalized) return true;
    if (this.currentUser?.id === 1) return true;
    const granted = new Set(this.permissions.map(p => p.trim()).filter(Boolean));
    if (granted.size === 0) return false;
    return granted.has(normalized);
  }

  hasAllPermissions(requiredKeys: string[]): boolean {
    if (this.isAdvertiser()) return false;
    const keys = requiredKeys.map(k => k.trim()).filter(Boolean);
    if (keys.length === 0) return true;
    if (this.currentUser?.id === 1) return true;
    return keys.every(k => this.hasPermission(k));
  }

  getApiErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const message = typeof err.error?.message === 'string' ? err.error.message.trim() : '';
      if (message) return message;
      if (typeof err.message === 'string' && err.message.trim()) return err.message.trim();
    }
    if (err instanceof Error && err.message.trim()) return err.message.trim();
    return fallback;
  }

  private setSession(session: AdminAuthSession): void {
    const normalized: AdminAuthSession = {
      token: session.token,
      user: session.user,
      role: session.role === 'advertiser' ? 'advertiser' : 'admin',
      permissions: Array.isArray(session.permissions) ? session.permissions : [],
    };
    this.sessionSubject.next(normalized);
  }

  private clearSession(): void {
    this.sessionSubject.next(null);
  }

  ensureSession(): Observable<boolean> {
    if (this.didManualLogout) return of(false);
    if (this.sessionRestoreRequest$) return this.sessionRestoreRequest$;

    this.sessionRestoreRequest$ = this.fetchMe().pipe(
      map(() => true),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          return this.refreshAccessToken().pipe(
            switchMap(ok => (ok ? this.fetchMe().pipe(map(() => true)) : of(false))),
          );
        }
        return of(false);
      }),
      finalize(() => {
        this.sessionRestoreRequest$ = undefined;
      }),
      shareReplay(1),
    );

    return this.sessionRestoreRequest$;
  }

  private fetchMe(): Observable<AdminAuthSession> {
    return this.http.get<AdminMeResponse>(`${environment.apiUrl}/admin/auth/me`, { withCredentials: true }).pipe(
      map(res => {
        const user = res?.data?.user;
        const role: AdminAuthRole = res?.data?.role === 'advertiser' ? 'advertiser' : 'admin';
        const permissions = Array.isArray(res?.data?.permissions) ? res.data.permissions : [];
        if (!user) throw new Error('Respuesta de sesión inválida');
        const token = this._pendingToken ?? this.token;
        this._pendingToken = null;
        const nextSession: AdminAuthSession = {
          token,
          user: {
            id: user.id,
            name: user.name,
            email: user.email,
            status: user.status,
            company_name: user.company_name ?? null,
            phone: user.phone ?? null,
          },
          role,
          permissions,
        };
        this.setSession(nextSession);
        return nextSession;
      }),
    );
  }
}
